import { useState, useEffect } from "react";
import { AddToHomeScreen, AddToHomeScreenOptions, DISPLAY_OPTIONS_DEFAULT, DeviceInfo, DeviceType } from "@/lib/add-to-homescreen";

const INSTALL_PROMPT_KEY = "snugglecrafters_install_prompt_dismissed";

export function useAddToHomeScreenPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  // Properly configure the battle-tested library with correct options
  const options: AddToHomeScreenOptions = {
    appName: "SnuggleCrafters",
    appIconUrl: "/logo.png", // Use existing SnuggleCrafters logo
    assetUrl: "/libs/add-to-homescreen/assets", // Proper assetUrl configuration
    maxModalDisplayCount: 3, // Show max 3 times (-1 for unlimited)
    appNameDisplay: "standalone", // Display app name standalone
    displayOptions: DISPLAY_OPTIONS_DEFAULT, // Use library's default options
    allowClose: true,
    showArrow: true
  };

  useEffect(() => {
    // Initialize the add-to-homescreen library instance
    const addToHomeScreen = AddToHomeScreen(options);

    // Use library's built-in standalone detection instead of custom logic
    if (addToHomeScreen.isStandAlone()) {
      setIsInstalled(true);
      // Mark as installed for future visits
      localStorage.setItem('snugglecrafters_app_installed', 'true');
      return;
    }

    // Check if user has previously installed the app
    const hasInstalled = localStorage.getItem('snugglecrafters_app_installed');
    if (hasInstalled === 'true') {
      setIsInstalled(true);
      return;
    }

    // Check if the prompt was previously dismissed permanently
    const dismissed = localStorage.getItem(INSTALL_PROMPT_KEY);
    if (dismissed === 'true') {
      return;
    }

    // Simple implementation for maximum display count check
    // Since hasReachedMaxModalDisplayCount may not be implemented, check manually
    const displayCountKey = 'snugglecrafters_modal_display_count';
    const currentCount = parseInt(localStorage.getItem(displayCountKey) || '0', 10);
    if (currentCount >= options.maxModalDisplayCount && options.maxModalDisplayCount > 0) {
      return;
    }

    // Use library's device detection without auto-showing the modal
    // Create device info manually to avoid triggering the modal
    const canBeStandalone = !addToHomeScreen.isStandAlone() && (
      addToHomeScreen.isDeviceIOS() || 
      addToHomeScreen.isDeviceAndroid() || 
      (addToHomeScreen.isDesktopChrome() && !!window.navigator.serviceWorker) ||
      (addToHomeScreen.isDesktopEdge() && !!window.navigator.serviceWorker)
    );
    
    const deviceType = addToHomeScreen.isDeviceIOS() ? DeviceType.IOS :
      addToHomeScreen.isDeviceAndroid() ? DeviceType.ANDROID : DeviceType.DESKTOP;
    
    const deviceInfo = new DeviceInfo(
      addToHomeScreen.isStandAlone(),
      canBeStandalone,
      deviceType
    );
    
    // Only show if device can be standalone and isn't already
    if (!deviceInfo.isStandAlone && deviceInfo.canBeStandAlone) {
      // Show prompt after a delay (10 seconds after page load)
      const timer = setTimeout(() => {
        // Increment display count
        const displayCountKey = 'snugglecrafters_modal_display_count';
        const currentCount = parseInt(localStorage.getItem(displayCountKey) || '0', 10);
        localStorage.setItem(displayCountKey, (currentCount + 1).toString());
        setShowPrompt(true);
      }, 10000);

      // Return cleanup function to clear timer when component unmounts
      return () => clearTimeout(timer);
    }
  }, []);

  const handleDontShowAgain = () => {
    localStorage.setItem(INSTALL_PROMPT_KEY, 'true');
    setShowPrompt(false);
  };

  const handleClose = () => {
    setShowPrompt(false);
  };

  const clearModalDisplayCount = () => {
    // Clear our manual modal display count
    localStorage.removeItem('snugglecrafters_modal_display_count');
  };

  const resetPromptSettings = () => {
    // Clear all stored prompt settings to allow showing again
    localStorage.removeItem(INSTALL_PROMPT_KEY);
    localStorage.removeItem('snugglecrafters_app_installed');
    clearModalDisplayCount();
  };

  return {
    showPrompt,
    isInstalled,
    handleClose,
    handleDontShowAgain,
    clearModalDisplayCount,
    resetPromptSettings,
    options,
  };
}